import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Modal, Row, Col, Badge, ListGroup } from 'react-bootstrap';
import { twoFactorAPI } from '../api/twoFactorAPI';
import TwoFactorSetup from './TwoFactorSetup';

function TwoFactorManagement() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSetup, setShowSetup] = useState(false);
    const [showDisableConfirm, setShowDisableConfirm] = useState(false);

    useEffect(() => {
        console.log('TwoFactorManagement: Component mounted, loading 2FA status...');
        loadStatus();
    }, []);

    const loadStatus = async () => {
        try {
            setLoading(true);
            console.log('TwoFactorManagement: Calling twoFactorAPI.getStatus()...');
            const data = await twoFactorAPI.getStatus();
            console.log('TwoFactorManagement: Received 2FA status:', data);
            setStatus(data);
            setError(null);
        } catch (error) {
            console.error('TwoFactorManagement: Error loading 2FA status:', error);
            setError('Failed to load 2FA status');
        } finally {
            setLoading(false);
        }
    };

    const handleSetupComplete = () => {
        setShowSetup(false);
        loadStatus();
    };

    const handleDisable = async () => {
        try {
            await twoFactorAPI.disable();
            setShowDisableConfirm(false);
            loadStatus();
        } catch (error) {
            setError('Failed to disable 2FA');
        }
    };


    if (loading) {
        return (
            <Card>
                <Card.Body className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading 2FA status...</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <div>
            <Card>
                <Card.Header>
                    <h5 className="mb-0">
                        <i className="fas fa-shield-alt me-2"></i>
                        Two-Factor Authentication
                    </h5>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                    
                    <div className="d-flex align-items-center mb-3">
                        <Badge 
                            bg={status?.is_2fa_enabled ? 'success' : 'secondary'}
                            className="me-3"
                        >
                            {status?.is_2fa_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <span>
                            {status?.is_2fa_enabled 
                                ? 'Your account is protected with two-factor authentication'
                                : 'Your account is not protected with two-factor authentication'
                            }
                        </span>
                    </div>
                    
                    {status?.is_2fa_enabled ? (
                        <div>
                            <p className="text-muted mb-3">
                                You have {status?.has_totp ? 'authenticator app ' : ''} 
                                {status?.has_totp && status?.has_static ? ' and ' : ''}
                                configured.
                            </p>
                            
                            <div className="d-flex gap-2">
                                <Button 
                                    variant="outline-danger" 
                                    onClick={() => setShowDisableConfirm(true)}
                                    size="sm"
                                >
                                    Disable 2FA
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p className="text-muted mb-3">
                                Enable two-factor authentication to add an extra layer of security to your account.
                            </p>
                            <Button 
                                variant="primary" 
                                onClick={() => setShowSetup(true)}
                            >
                                Enable 2FA
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Setup Modal */}
            <TwoFactorSetup 
                show={showSetup} 
                onHide={() => setShowSetup(false)} 
                onComplete={handleSetupComplete}
            />


            {/* Disable Confirmation Modal */}
            <Modal show={showDisableConfirm} onHide={() => setShowDisableConfirm(false)} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Disable Two-Factor Authentication
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <h6>Are you sure you want to disable 2FA?</h6>
                        <p className="mb-0">
                            This will remove the extra security layer from your account. 
                            Your account will be less secure without two-factor authentication.
                        </p>
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowDisableConfirm(false)}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDisable}
                    >
                        Yes, Disable 2FA
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default TwoFactorManagement;
